import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { from, map, mergeMap, Observable, tap } from 'rxjs';
import { PageImageLoadingPrioritizer } from './page-image-loading-prioritizer';
import { PAGE_IMAGE_LOADER_TOKEN } from '../model/page-image-loader.interface';
import { OcrService } from './ocr.service';

export const MAX_CONCURRENT_REQUESTS = 6;

interface ImageData {
  thumbnailSrc: WritableSignal<string | undefined>;
  src: WritableSignal<string | undefined>;
  isLoaded: Signal<boolean>;
  isThumbnailLoaded: Signal<boolean>;
  aspectRatio: WritableSignal<number>;
}

@Injectable({ providedIn: 'root' })
export class PageImageService {
  private readonly imageDataByPageId = new Map<string, ImageData>();

  private readonly defaultImage = '@shared/logos/empty-page.png';

  private readonly pageImageLoadingPrioritizer = inject(PageImageLoadingPrioritizer);
  private readonly ocrService = inject(OcrService);
  private readonly pageImageLoader = inject(PAGE_IMAGE_LOADER_TOKEN);

  init(pageIds: string[]) {
    this.pageImageLoadingPrioritizer.init(pageIds);

    this.initImageData(pageIds);
  }

  loadAroundPage(pageId: string) {
    const { pageIdsToLoad, pageIdsToUnload } =
      this.pageImageLoadingPrioritizer.requestLoadAroundPage(pageId);
    this.loadImageBlobs(pageIdsToLoad);
    for (const pageIdToUnload of pageIdsToUnload) {
      const url = this.imageDataByPageId.get(pageIdToUnload)?.src();
      if (url) {
        URL.revokeObjectURL(url);
        this.imageDataByPageId.get(pageIdToUnload)?.src.set(this.defaultImage);
      }
    }
  }

  private initializeImageData(pageId: string) {
    const src = signal(this.defaultImage);
    const thumbnailSrc = signal(this.defaultImage);
    this.imageDataByPageId.set(pageId, {
      src,
      thumbnailSrc: thumbnailSrc,
      aspectRatio: signal(0.7),
      isThumbnailLoaded: computed(() => thumbnailSrc() !== this.defaultImage),
      isLoaded: computed(() => src() !== this.defaultImage),
    });
  }

  private loadImageBlobs(pageIds: Iterable<string>) {
    from(pageIds)
      .pipe(
        mergeMap((id) => this.loadImageBlob(id), MAX_CONCURRENT_REQUESTS),
        mergeMap(() => this.ocrService.loadOcrDataForPageIds(pageIds)),
      )
      .subscribe();
  }

  private loadImageBlob(pageId: string): Observable<string | undefined> {
    return this.pageImageLoader.getImageForPage(pageId).pipe(
      map((imageBlob) => URL.createObjectURL(imageBlob)),
      tap((objectUrl) => this.imageDataByPageId.get(pageId)!.src.set(objectUrl)),
    );
  }

  getImageSrc(pageId: string): Signal<string | undefined> {
    return this.getImageData(pageId).src.asReadonly();
  }

  getThumbnailImageSrc(id: string): Signal<string | undefined> {
    return this.getImageData(id).thumbnailSrc.asReadonly();
  }

  isImageLoaded(pageId: string): Signal<boolean> {
    return this.getImageData(pageId).isLoaded;
  }

  isThumbnailImageLoaded(pageId: string): Signal<boolean> {
    return this.getImageData(pageId).isThumbnailLoaded;
  }

  getImageAspectRatio(pageId: string): Signal<number> {
    return this.getImageData(pageId).aspectRatio.asReadonly();
  }

  /**
   * Updates the aspect ratio of an image. There are basically two options:
   *
   * * Either we store this information in the database and transmit it together with the images
   * * Or we use the <img/> element's naturalWidth and naturalHeight to calculate this value
   *
   * Right now we use the latter approach.
   */
  updateImageAspectRatio(pageId: string, aspectRatio: number) {
    this.getImageData(pageId).aspectRatio.set(aspectRatio);
  }

  getImageData(pageId: string): ImageData {
    if (!this.imageDataByPageId.has(pageId)) {
      throw new Error(`image data requested for id '${pageId}' which does not exist in data map`);
    }

    return this.imageDataByPageId.get(pageId)!;
  }

  initImageData(pageIds: string[]) {
    pageIds
      .filter((id) => !this.imageDataByPageId.has(id))
      .forEach((pageId) => this.initializeImageData(pageId));
  }
}
